import { useSyncExternalStore } from "react";

/**
 * Whether this page is in the middle of signing out.
 *
 * The session ends on the server before the device is cleared (a refused
 * sign-out must leave everything in place). While this is set, the shell
 * covers the app with a spinner, so nothing is written that the clear would
 * discard, and once the session is gone it shows the spinner in place of the
 * sign-in screen, so the first one anyone sees is the fresh page the sign-out
 * lands on, after the device is clear.
 *
 * Lives outside device-replica.ts so the pre-login bundle can read it without
 * pulling in PowerSync.
 */

let isSigningOut = false;
const listeners = new Set<() => void>();

export function setSigningOut(value: boolean): void {
  isSigningOut = value;
  for (const listener of listeners) {
    listener();
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): boolean {
  return isSigningOut;
}

let isReloadingPage = false;

/**
 * Record that this page is about to be replaced by a fresh one it asked for:
 * a sign-out whose session is over, or a replica handed to another account
 * (device-replica.ts). Nothing on the page may hold that load back.
 */
export function markPageReloading(): void {
  isReloadingPage = true;
}

/**
 * Replace this page with a fresh one, standing the unsaved-changes guards
 * down first. Nothing else stands them down, so a caller that reloads without
 * this is either refused by the browser or, in a webview where the prompt is
 * suppressed, destroys a draft without asking.
 */
export function reloadApp(): void {
  markPageReloading();
  window.location.reload();
}

/**
 * For the unsaved-changes guards: once the page is being replaced by the one
 * a finished sign-out or an account hand-over asked for, a "Leave page?"
 * prompt would only cancel that load. A sign-out still in progress keeps them,
 * since it can yet be cancelled or refused.
 */
export function isLeavingPage(): boolean {
  return isReloadingPage;
}

export function useIsSigningOut(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
